/**
 * StylePanel.js
 *
 * Panel derecho: Contenido / Estilo / Avanzado del nodo seleccionado.
 *
 * Dos decisiones que definen cómo se siente:
 *
 * 1. Todo selector de color ofrece PRIMERO los colores del tema. Empujar al usuario
 *    hacia el token en vez del hex es lo que mantiene el sitio coherente y hace que
 *    re-marcar todo para otro negocio sea cambiar cinco campos, no doscientos.
 *
 * 2. Cada control muestra si el valor está HEREDADO del desktop o sobreescrito en
 *    el breakpoint actual, con un botón para volver a heredar. Sin esa señal, la
 *    edición responsive es adivinanza: se cambia algo en móvil, se vuelve a
 *    escritorio y no se entiende por qué no pasó nada.
 */

import React, { useState } from 'react';
import {
  effectiveStyle, isOverridden, FONT_OPTIONS, COLOR_PRESETS, FONT_PAIRS
} from './builderModel';
import { ICON_NAMES } from './blockLibrary';
import { Icon } from './ElementRenderer';

const TYPE_LABELS = {
  section: 'Sección', row: 'Fila', column: 'Columna',
  heading: 'Título', text: 'Texto', button: 'Botón', image: 'Imagen', video: 'Video',
  icon: 'Icono', divider: 'Separador', spacer: 'Espacio', list: 'Lista', form: 'Formulario',
  booking: 'Agendar', map: 'Mapa', social: 'Redes', html: 'HTML', countdown: 'Cuenta regresiva',
  testimonial: 'Testimonio', pricing: 'Plan', faq: 'Preguntas'
};

/* ------------------------------------------------------------------ *
 * Controles base
 * ------------------------------------------------------------------ */

function Field({ label, children, hint, overridden, onReset }) {
  return (
    <div className="sp-field">
      <div className="sp-label">
        <span>{label}</span>
        {overridden && onReset ? (
          <button type="button" className="sp-reset" onClick={onReset} title="Volver a heredar del escritorio">
            heredar
          </button>
        ) : null}
      </div>
      {children}
      {hint ? <small className="sp-hint">{hint}</small> : null}
    </div>
  );
}

function ColorInput({ value, onChange, theme, allowEmpty }) {
  const [open, setOpen] = useState(false);
  const tokens = Object.entries(theme?.colors || {});
  const isToken = typeof value === 'string' && value.startsWith('var(--c-');
  const hex = isToken || !value ? '#000000' : value;

  return (
    <div className="sp-color">
      <button type="button" className="sp-swatch" style={{ background: value || 'transparent' }}
        onClick={() => setOpen(!open)} title={value || 'sin color'}>
        {!value ? '∅' : ''}
      </button>
      <input className="sp-input" value={value || ''} placeholder="var(--c-primary) o #RRGGBB"
        onChange={(e) => onChange(e.target.value)} />
      {open && (
        <div className="sp-pop">
          <div className="sp-pop-title">Colores del tema</div>
          <div className="sp-tokens">
            {tokens.map(([k, v]) => (
              <button type="button" key={k} title={k} style={{ background: v }}
                className={value === 'var(--c-' + k + ')' ? 'on' : ''}
                onClick={() => { onChange('var(--c-' + k + ')'); setOpen(false); }} />
            ))}
          </div>
          <div className="sp-pop-title">Personalizado</div>
          <input type="color" value={hex} onChange={(e) => onChange(e.target.value)} />
          <div className="sp-pop-row">
            {allowEmpty && <button type="button" onClick={() => { onChange(''); setOpen(false); }}>Sin color</button>}
            <button type="button" onClick={() => setOpen(false)}>Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function NumberInput({ value, onChange, min, max, step, suffix, placeholder }) {
  return (
    <div className="sp-num">
      <input type="number" className="sp-input" value={value ?? ''} placeholder={placeholder || 'auto'}
        min={min} max={max} step={step || 1}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
      {suffix ? <span>{suffix}</span> : null}
    </div>
  );
}

function Segmented({ value, options, onChange }) {
  return (
    <div className="sp-seg">
      {options.map((o) => (
        <button type="button" key={o.value}
          className={value === o.value ? 'on' : ''}
          onClick={() => onChange(o.value)} title={o.title || o.label}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, multiline, rows }) {
  return multiline
    ? <textarea className="sp-input" rows={rows || 3} value={value || ''} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    : <input className="sp-input" value={value || ''} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />;
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="sp-toggle">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

/* ------------------------------------------------------------------ *
 * Pestaña CONTENIDO
 * ------------------------------------------------------------------ */

function ContentTab({ node, setProps, theme, calendars }) {
  const p = node.props || {};
  const set = (patch) => setProps(node.id, patch);

  switch (node.type) {
    case 'section':
      return (
        <>
          <Field label="Ancla (para enlaces #)" hint="Un botón puede apuntar aquí con esta palabra.">
            <TextInput value={p.anchor} onChange={(v) => set({ anchor: v.replace(/[^a-zA-Z0-9_-]/g, '') })} placeholder="contacto" />
          </Field>
        </>
      );

    case 'row':
      return (
        <Field label="Ancho máximo del contenido" hint="0 = usa el ancho del tema.">
          <NumberInput value={p.maxWidth} onChange={(v) => set({ maxWidth: v || 0 })} suffix="px" min={0} max={1920} />
        </Field>
      );

    case 'column':
      return (
        <>
          <Field label="Ancho en escritorio"><NumberInput value={p.widthDesktop} onChange={(v) => set({ widthDesktop: v || 100 })} suffix="%" min={5} max={100} /></Field>
          <Field label="Ancho en tablet" hint="0 = hereda el de escritorio."><NumberInput value={p.widthTablet} onChange={(v) => set({ widthTablet: v || 0 })} suffix="%" min={0} max={100} /></Field>
          <Field label="Ancho en móvil"><NumberInput value={p.widthMobile} onChange={(v) => set({ widthMobile: v || 100 })} suffix="%" min={5} max={100} /></Field>
        </>
      );

    case 'heading':
      return (
        <>
          <Field label="Texto"><TextInput value={p.text} onChange={(v) => set({ text: v })} multiline /></Field>
          <Field label="Jerarquía" hint="H1 solo una vez por página: es la que lee Google.">
            <Segmented value={p.tag} onChange={(v) => set({ tag: v })}
              options={['h1', 'h2', 'h3', 'h4'].map((t) => ({ value: t, label: t.toUpperCase() }))} />
          </Field>
        </>
      );

    case 'text':
      return <Field label="Contenido" hint="También puedes escribir directo sobre el lienzo.">
        <TextInput value={p.html} onChange={(v) => set({ html: v })} multiline rows={6} />
      </Field>;

    case 'button':
      return (
        <>
          <Field label="Texto"><TextInput value={p.label} onChange={(v) => set({ label: v })} /></Field>
          <Field label="Al hacer clic">
            <select className="sp-input" value={p.action} onChange={(e) => set({ action: e.target.value })}>
              <option value="url">Ir a una dirección web</option>
              <option value="anchor">Bajar a una sección</option>
              <option value="whatsapp">Abrir WhatsApp</option>
              <option value="phone">Llamar</option>
              <option value="email">Enviar correo</option>
            </select>
          </Field>
          {p.action === 'url' && <Field label="Dirección"><TextInput value={p.url} onChange={(v) => set({ url: v })} placeholder="https://…" /></Field>}
          {p.action === 'anchor' && <Field label="Ancla de la sección"><TextInput value={p.anchor} onChange={(v) => set({ anchor: v })} placeholder="contacto" /></Field>}
          {(p.action === 'whatsapp' || p.action === 'phone') && (
            <Field label="Número"><TextInput value={p.phone} onChange={(v) => set({ phone: v })} placeholder="593999999999" /></Field>
          )}
          {p.action === 'whatsapp' && (
            <Field label="Mensaje previo" hint="Se abre ya escrito en el chat.">
              <TextInput value={p.message} onChange={(v) => set({ message: v })} multiline />
            </Field>
          )}
          {p.action === 'email' && <Field label="Correo"><TextInput value={p.email} onChange={(v) => set({ email: v })} /></Field>}
          <Field label="Estilo">
            <Segmented value={p.variant || 'solid'} onChange={(v) => set({ variant: v })}
              options={[{ value: 'solid', label: 'Sólido' }, { value: 'outline', label: 'Borde' }, { value: 'ghost', label: 'Texto' }]} />
          </Field>
          <Toggle checked={p.newTab} onChange={(v) => set({ newTab: v })} label="Abrir en otra pestaña" />
        </>
      );

    case 'image':
      return (
        <>
          <Field label="Dirección de la imagen" hint="Pega una URL o sube la foto a tu hosting.">
            <TextInput value={p.src} onChange={(v) => set({ src: v })} placeholder="https://…" />
          </Field>
          {p.src ? <img className="sp-preview" src={p.src} alt="" /> : null}
          <Field label="Texto alternativo" hint="Lo lee Google y quien usa lector de pantalla.">
            <TextInput value={p.alt} onChange={(v) => set({ alt: v })} />
          </Field>
          <Field label="Enlace al hacer clic"><TextInput value={p.link} onChange={(v) => set({ link: v })} placeholder="opcional" /></Field>
        </>
      );

    case 'video':
      return <Field label="Enlace del video" hint="YouTube, Vimeo o un archivo .mp4.">
        <TextInput value={p.src} onChange={(v) => set({ src: v })} placeholder="https://youtube.com/watch?v=…" />
      </Field>;

    case 'icon':
      return (
        <>
          <Field label="Icono">
            <div className="sp-icons">
              {ICON_NAMES.map((n) => (
                <button type="button" key={n} className={p.name === n ? 'on' : ''} onClick={() => set({ name: n })} title={n}>
                  <Icon name={n} size={18} />
                  <span>{n}</span>
                </button>
              ))}
            </div>
          </Field>
          <Field label="Tamaño"><NumberInput value={p.size} onChange={(v) => set({ size: v || 32 })} suffix="px" min={12} max={160} /></Field>
        </>
      );

    case 'divider':
      return <Field label="Grosor"><NumberInput value={p.thickness} onChange={(v) => set({ thickness: v || 1 })} suffix="px" min={1} max={20} /></Field>;

    case 'spacer':
      return <Field label="Altura"><NumberInput value={p.height} onChange={(v) => set({ height: v || 40 })} suffix="px" min={4} max={400} /></Field>;

    case 'list':
      return (
        <>
          <Field label="Icono de viñeta">
            <select className="sp-input" value={p.icon || ''} onChange={(e) => set({ icon: e.target.value })}>
              <option value="">Sin icono</option>
              {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          <Field label="Ítems" hint="Uno por línea. También se editan sobre el lienzo.">
            <TextInput multiline rows={6} value={(p.items || []).join('\n')}
              onChange={(v) => set({ items: v.split('\n') })} />
          </Field>
        </>
      );

    case 'form':
      return <FormFields node={node} set={set} />;

    case 'booking':
      return (
        <>
          <Field label="Calendario">
            <select className="sp-input" value={p.bookingSlug || ''} onChange={(e) => set({ bookingSlug: e.target.value })}>
              <option value="">— elige un calendario —</option>
              {(calendars || []).map((c) => (
                <option key={c.id} value={c.public_slug || ''} disabled={!c.public_slug}>
                  {c.name}{c.public_slug ? '' : ' (sin dirección pública)'}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Altura"><NumberInput value={p.height} onChange={(v) => set({ height: v || 720 })} suffix="px" min={400} max={1400} /></Field>
        </>
      );

    case 'map':
      return (
        <>
          <Field label="Dirección o lugar"><TextInput value={p.query} onChange={(v) => set({ query: v })} placeholder="Av. Principal 123, Guayaquil" /></Field>
          <Field label="Altura"><NumberInput value={p.height} onChange={(v) => set({ height: v || 320 })} suffix="px" min={160} max={800} /></Field>
        </>
      );

    case 'social':
      return (
        <Field label="Redes">
          {(p.links || []).map((l, i) => (
            <div className="sp-row" key={i}>
              <select className="sp-input" value={l.network}
                onChange={(e) => {
                  const links = (p.links || []).slice();
                  links[i] = Object.assign({}, links[i], { network: e.target.value });
                  set({ links });
                }}>
                {['whatsapp', 'instagram', 'facebook', 'tiktok', 'mail', 'phone'].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <input className="sp-input" value={l.url} placeholder="https://…"
                onChange={(e) => {
                  const links = (p.links || []).slice();
                  links[i] = Object.assign({}, links[i], { url: e.target.value });
                  set({ links });
                }} />
              <button type="button" className="sp-x" onClick={() => set({ links: (p.links || []).filter((_, j) => j !== i) })}>×</button>
            </div>
          ))}
          <button type="button" className="sp-add" onClick={() => set({ links: (p.links || []).concat([{ network: 'instagram', url: '' }]) })}>
            + Agregar red
          </button>
        </Field>
      );

    case 'html':
      return <Field label="Código HTML" hint="Se quitan los scripts al publicar, por seguridad.">
        <TextInput multiline rows={10} value={p.code} onChange={(v) => set({ code: v })} />
      </Field>;

    case 'countdown':
      return (
        <>
          <Field label="Fecha y hora objetivo">
            <input type="datetime-local" className="sp-input" value={p.target || ''} onChange={(e) => set({ target: e.target.value })} />
          </Field>
          <Field label="Texto al terminar"><TextInput value={p.expiredText} onChange={(v) => set({ expiredText: v })} /></Field>
        </>
      );

    case 'testimonial':
      return (
        <>
          <Field label="Testimonio"><TextInput multiline rows={4} value={p.quote} onChange={(v) => set({ quote: v })} /></Field>
          <Field label="Autor"><TextInput value={p.author} onChange={(v) => set({ author: v })} /></Field>
          <Field label="Cargo o referencia"><TextInput value={p.role} onChange={(v) => set({ role: v })} /></Field>
          <Field label="Foto (URL)"><TextInput value={p.avatar} onChange={(v) => set({ avatar: v })} /></Field>
          <Field label="Estrellas"><NumberInput value={p.stars} onChange={(v) => set({ stars: v })} min={0} max={5} /></Field>
        </>
      );

    case 'pricing':
      return (
        <>
          <Field label="Nombre del plan"><TextInput value={p.plan} onChange={(v) => set({ plan: v })} /></Field>
          <Field label="Precio"><TextInput value={p.price} onChange={(v) => set({ price: v })} placeholder="$99" /></Field>
          <Field label="Periodo"><TextInput value={p.period} onChange={(v) => set({ period: v })} placeholder="/mes" /></Field>
          <Field label="Características"><TextInput multiline rows={5} value={(p.features || []).join('\n')} onChange={(v) => set({ features: v.split('\n') })} /></Field>
          <Field label="Texto del botón"><TextInput value={p.ctaLabel} onChange={(v) => set({ ctaLabel: v })} /></Field>
          <Field label="Enlace del botón"><TextInput value={p.ctaUrl} onChange={(v) => set({ ctaUrl: v })} /></Field>
          <Toggle checked={p.highlighted} onChange={(v) => set({ highlighted: v })} label="Destacar este plan" />
        </>
      );

    case 'faq':
      return (
        <Field label="Preguntas">
          {(p.items || []).map((it, i) => (
            <div className="sp-card" key={i}>
              <input className="sp-input" value={it.q} placeholder="Pregunta"
                onChange={(e) => {
                  const items = (p.items || []).slice();
                  items[i] = Object.assign({}, items[i], { q: e.target.value });
                  set({ items });
                }} />
              <textarea className="sp-input" rows={2} value={it.a} placeholder="Respuesta"
                onChange={(e) => {
                  const items = (p.items || []).slice();
                  items[i] = Object.assign({}, items[i], { a: e.target.value });
                  set({ items });
                }} />
              <button type="button" className="sp-x" onClick={() => set({ items: (p.items || []).filter((_, j) => j !== i) })}>Eliminar</button>
            </div>
          ))}
          <button type="button" className="sp-add" onClick={() => set({ items: (p.items || []).concat([{ q: '', a: '' }]) })}>+ Agregar pregunta</button>
        </Field>
      );

    default:
      return <p className="sp-empty">Este bloque no tiene opciones de contenido.</p>;
  }
}

/* ---- editor de campos del formulario ---- */

function FormFields({ node, set }) {
  const p = node.props || {};
  const fields = p.fields || [];
  const update = (i, patch) => {
    const next = fields.slice();
    next[i] = Object.assign({}, next[i], patch);
    set({ fields: next });
  };
  const mapped = new Set(fields.map((f) => f.mapTo).filter(Boolean));

  return (
    <>
      <Field label="Título"><TextInput value={p.title} onChange={(v) => set({ title: v })} /></Field>
      <Field label="Texto del botón"><TextInput value={p.submitLabel} onChange={(v) => set({ submitLabel: v })} /></Field>
      <Field label="Mensaje de éxito"><TextInput value={p.successMessage} onChange={(v) => set({ successMessage: v })} /></Field>
      <Field label="Redirigir a" hint="Si lo dejas vacío se muestra el mensaje de éxito.">
        <TextInput value={p.redirectUrl} onChange={(v) => set({ redirectUrl: v })} placeholder="/p/tu-sitio/gracias" />
      </Field>
      <Field label="Etiqueta que se aplica al contacto" hint="Los workflows pueden dispararse con esta etiqueta.">
        <TextInput value={p.tagName} onChange={(v) => set({ tagName: v })} placeholder="Lead Web" />
      </Field>

      {!mapped.has('phone') && !mapped.has('email') && (
        <p className="sp-warn">
          Ningún campo está conectado a WhatsApp ni a correo: el lead se guardará, pero no se creará
          el contacto en el CRM y no habrá cómo responderle.
        </p>
      )}

      <div className="sp-label"><span>Campos</span></div>
      {fields.map((f, i) => (
        <div className="sp-card" key={i}>
          <input className="sp-input" value={f.label} placeholder="Etiqueta visible"
            onChange={(e) => update(i, { label: e.target.value })} />
          <div className="sp-row">
            <select className="sp-input" value={f.type} onChange={(e) => update(i, { type: e.target.value })}>
              <option value="text">Texto</option>
              <option value="tel">Teléfono</option>
              <option value="email">Correo</option>
              <option value="textarea">Párrafo</option>
              <option value="select">Lista</option>
              <option value="checkbox">Casilla</option>
              <option value="date">Fecha</option>
              <option value="number">Número</option>
            </select>
            <select className="sp-input" value={f.mapTo || ''} onChange={(e) => update(i, { mapTo: e.target.value })}>
              <option value="">No guardar en contacto</option>
              <option value="name">→ Nombre</option>
              <option value="phone">→ WhatsApp</option>
              <option value="email">→ Correo</option>
              <option value="notes">→ Notas</option>
            </select>
          </div>
          {f.type === 'select' && (
            <textarea className="sp-input" rows={2} placeholder="Una opción por línea"
              value={(f.options || []).join('\n')}
              onChange={(e) => update(i, { options: e.target.value.split('\n') })} />
          )}
          <div className="sp-row">
            <Toggle checked={f.required} onChange={(v) => update(i, { required: v })} label="Obligatorio" />
            <button type="button" className="sp-x" onClick={() => set({ fields: fields.filter((_, j) => j !== i) })}>Eliminar</button>
          </div>
        </div>
      ))}
      <button type="button" className="sp-add"
        onClick={() => set({ fields: fields.concat([{ key: 'campo' + (fields.length + 1), label: 'Nuevo campo', type: 'text', required: false, mapTo: '' }]) })}>
        + Agregar campo
      </button>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Pestaña ESTILO
 * ------------------------------------------------------------------ */

const ALIGN_OPTS = [
  { value: 'left', label: '⟵' }, { value: 'center', label: '↔' },
  { value: 'right', label: '⟶' }, { value: 'justify', label: '≡' }
];

function StyleTab({ node, breakpoint, setStyle, theme }) {
  const eff = effectiveStyle(node, breakpoint);
  const s = (key, value) => setStyle(node.id, breakpoint, key, value);
  const ov = (key) => isOverridden(node, breakpoint, key);
  const reset = (key) => () => setStyle(node.id, breakpoint, key, null);
  const isText = ['heading', 'text', 'button', 'list', 'pricing', 'testimonial', 'faq', 'icon'].includes(node.type);

  return (
    <>
      {breakpoint !== 'desktop' && (
        <p className="sp-note">
          Estás editando la vista <b>{breakpoint === 'tablet' ? 'tablet' : 'móvil'}</b>.
          Lo que cambies aquí solo aplica a ese tamaño.
        </p>
      )}

      {isText && (
        <details open className="sp-group">
          <summary>Tipografía</summary>
          <Field label="Fuente" overridden={ov('fontFamily')} onReset={reset('fontFamily')}>
            <select className="sp-input" value={eff.fontFamily || ''} onChange={(e) => s('fontFamily', e.target.value)}>
              <option value="">Del tema</option>
              <option value="var(--f-heading)">Fuente de títulos</option>
              <option value="var(--f-body)">Fuente de texto</option>
              {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <div className="sp-row">
            <Field label="Tamaño" overridden={ov('fontSize')} onReset={reset('fontSize')}>
              <NumberInput value={eff.fontSize} onChange={(v) => s('fontSize', v)} suffix="px" min={8} max={140} />
            </Field>
            <Field label="Grosor" overridden={ov('fontWeight')} onReset={reset('fontWeight')}>
              <select className="sp-input" value={eff.fontWeight || ''} onChange={(e) => s('fontWeight', e.target.value ? Number(e.target.value) : null)}>
                <option value="">auto</option>
                {[300, 400, 500, 600, 700, 800].map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </Field>
          </div>
          <div className="sp-row">
            <Field label="Interlineado" overridden={ov('lineHeight')} onReset={reset('lineHeight')}>
              <NumberInput value={eff.lineHeight} onChange={(v) => s('lineHeight', v)} step={0.1} min={0.8} max={3} />
            </Field>
            <Field label="Espaciado" overridden={ov('letterSpacing')} onReset={reset('letterSpacing')}>
              <NumberInput value={eff.letterSpacing} onChange={(v) => s('letterSpacing', v)} suffix="px" min={-3} max={20} step={0.5} />
            </Field>
          </div>
          <Field label="Alineación" overridden={ov('textAlign')} onReset={reset('textAlign')}>
            <Segmented value={eff.textAlign} options={ALIGN_OPTS} onChange={(v) => s('textAlign', v)} />
          </Field>
          <Field label="Color del texto" overridden={ov('color')} onReset={reset('color')}>
            <ColorInput value={eff.color} onChange={(v) => s('color', v)} theme={theme} allowEmpty />
          </Field>
          <Field label="Mayúsculas" overridden={ov('textTransform')} onReset={reset('textTransform')}>
            <Segmented value={eff.textTransform || 'none'} onChange={(v) => s('textTransform', v === 'none' ? null : v)}
              options={[{ value: 'none', label: 'Aa' }, { value: 'uppercase', label: 'AA' }, { value: 'lowercase', label: 'aa' }]} />
          </Field>
        </details>
      )}

      <details open className="sp-group">
        <summary>Espaciado</summary>
        <div className="sp-box">
          <span className="sp-box-title">Relleno interno (padding)</span>
          <div className="sp-grid4">
            {['Top', 'Right', 'Bottom', 'Left'].map((side) => (
              <Field key={side} label={{ Top: 'Arriba', Right: 'Der.', Bottom: 'Abajo', Left: 'Izq.' }[side]}
                overridden={ov('padding' + side)} onReset={reset('padding' + side)}>
                <NumberInput value={eff['padding' + side]} onChange={(v) => s('padding' + side, v)} min={0} max={400} />
              </Field>
            ))}
          </div>
        </div>
        <div className="sp-box">
          <span className="sp-box-title">Margen externo</span>
          <div className="sp-grid4">
            {['Top', 'Right', 'Bottom', 'Left'].map((side) => (
              <Field key={side} label={{ Top: 'Arriba', Right: 'Der.', Bottom: 'Abajo', Left: 'Izq.' }[side]}
                overridden={ov('margin' + side)} onReset={reset('margin' + side)}>
                <NumberInput value={eff['margin' + side]} onChange={(v) => s('margin' + side, v)} min={-200} max={400} />
              </Field>
            ))}
          </div>
        </div>
      </details>

      <details className="sp-group">
        <summary>Fondo y borde</summary>
        <Field label="Color de fondo" overridden={ov('backgroundColor')} onReset={reset('backgroundColor')}>
          <ColorInput value={eff.backgroundColor} onChange={(v) => s('backgroundColor', v)} theme={theme} allowEmpty />
        </Field>
        <Field label="Degradado o fondo avanzado" overridden={ov('background')} onReset={reset('background')}
          hint="Ej: linear-gradient(135deg, var(--c-primary), var(--c-secondary))">
          <TextInput value={eff.background} onChange={(v) => s('background', v)} />
        </Field>
        <Field label="Imagen de fondo (URL)" overridden={ov('backgroundImage')} onReset={reset('backgroundImage')}>
          <TextInput value={(eff.backgroundImage || '').replace(/^url\((.*)\)$/, '$1')}
            onChange={(v) => s('backgroundImage', v ? 'url(' + v + ')' : null)} />
        </Field>
        {eff.backgroundImage && (
          <div className="sp-row">
            <Field label="Ajuste"><Segmented value={eff.backgroundSize || 'cover'} onChange={(v) => s('backgroundSize', v)}
              options={[{ value: 'cover', label: 'Cubrir' }, { value: 'contain', label: 'Contener' }]} /></Field>
            <Field label="Posición"><TextInput value={eff.backgroundPosition} onChange={(v) => s('backgroundPosition', v)} placeholder="center" /></Field>
          </div>
        )}
        <div className="sp-row">
          <Field label="Radio" overridden={ov('borderRadius')} onReset={reset('borderRadius')}>
            <TextInput value={eff.borderRadius} onChange={(v) => s('borderRadius', v)} placeholder="var(--radius)" />
          </Field>
          <Field label="Grosor borde" overridden={ov('borderWidth')} onReset={reset('borderWidth')}>
            <NumberInput value={eff.borderWidth} onChange={(v) => { s('borderWidth', v); if (v && !eff.borderStyle) s('borderStyle', 'solid'); }} suffix="px" min={0} max={20} />
          </Field>
        </div>
        <Field label="Color del borde" overridden={ov('borderColor')} onReset={reset('borderColor')}>
          <ColorInput value={eff.borderColor} onChange={(v) => s('borderColor', v)} theme={theme} allowEmpty />
        </Field>
        <Field label="Sombra" overridden={ov('boxShadow')} onReset={reset('boxShadow')}>
          <select className="sp-input" value={eff.boxShadow || ''} onChange={(e) => s('boxShadow', e.target.value || null)}>
            <option value="">Sin sombra</option>
            <option value="0 2px 8px rgba(0,0,0,.06)">Sutil</option>
            <option value="0 10px 30px rgba(0,0,0,.08)">Media</option>
            <option value="0 20px 50px rgba(0,0,0,.15)">Pronunciada</option>
          </select>
        </Field>
      </details>

      <details className="sp-group">
        <summary>Tamaño y disposición</summary>
        <div className="sp-row">
          <Field label="Ancho" overridden={ov('width')} onReset={reset('width')}>
            <TextInput value={eff.width} onChange={(v) => s('width', v)} placeholder="auto / 100% / 320px" />
          </Field>
          <Field label="Alto mínimo" overridden={ov('minHeight')} onReset={reset('minHeight')}>
            <NumberInput value={eff.minHeight} onChange={(v) => s('minHeight', v)} suffix="px" min={0} max={1200} />
          </Field>
        </div>
        {node.type === 'row' && (
          <>
            <Field label="Separación entre columnas" overridden={ov('gap')} onReset={reset('gap')}>
              <NumberInput value={eff.gap} onChange={(v) => s('gap', v)} suffix="px" min={0} max={120} />
            </Field>
            <Field label="Alineación vertical" overridden={ov('alignItems')} onReset={reset('alignItems')}>
              <Segmented value={eff.alignItems || 'flex-start'} onChange={(v) => s('alignItems', v)}
                options={[{ value: 'flex-start', label: 'Arriba' }, { value: 'center', label: 'Centro' }, { value: 'stretch', label: 'Estirar' }]} />
            </Field>
          </>
        )}
        <Field label="Opacidad" overridden={ov('opacity')} onReset={reset('opacity')}>
          <NumberInput value={eff.opacity} onChange={(v) => s('opacity', v)} min={0} max={1} step={0.05} />
        </Field>
      </details>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Pestaña AVANZADO
 * ------------------------------------------------------------------ */

function AdvancedTab({ node, setAdvanced }) {
  const a = node.advanced || {};
  const set = (patch) => setAdvanced(node.id, patch);
  return (
    <>
      <div className="sp-label"><span>Visibilidad por dispositivo</span></div>
      <Toggle checked={a.hideDesktop} onChange={(v) => set({ hideDesktop: v })} label="Ocultar en escritorio" />
      <Toggle checked={a.hideTablet} onChange={(v) => set({ hideTablet: v })} label="Ocultar en tablet" />
      <Toggle checked={a.hideMobile} onChange={(v) => set({ hideMobile: v })} label="Ocultar en móvil" />
      <Field label="Animación de entrada">
        <select className="sp-input" value={a.animation || ''} onChange={(e) => set({ animation: e.target.value })}>
          <option value="">Sin animación</option>
          <option value="fade">Aparecer</option>
          <option value="up">Subir</option>
          <option value="left">Desde la izquierda</option>
          <option value="right">Desde la derecha</option>
          <option value="zoom">Acercar</option>
        </select>
      </Field>
      <Field label="Clase CSS propia" hint="Solo si sabes lo que haces.">
        <TextInput value={a.cssClass} onChange={(v) => set({ cssClass: v })} />
      </Field>
      <div className="sp-id">ID interno: <code>{node.id}</code></div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Pestaña TEMA (sin selección)
 * ------------------------------------------------------------------ */

export function ThemePanel({ theme, onTheme }) {
  const setColor = (k, v) => onTheme(Object.assign({}, theme, { colors: Object.assign({}, theme.colors, { [k]: v }) }));
  const setFont = (slot, family) => onTheme(Object.assign({}, theme, {
    fonts: Object.assign({}, theme.fonts, { [slot]: Object.assign({}, theme.fonts[slot], { family }) })
  }));

  return (
    <div className="sp-body">
      <p className="sp-note">
        Esto cambia <b>todo el sitio de una vez</b>. Los bloques que usan colores del tema se
        actualizan solos; los que tienen un color propio se quedan como están.
      </p>

      <div className="sp-label"><span>Paletas listas</span></div>
      <div className="sp-presets">
        {COLOR_PRESETS.map((p) => (
          <button type="button" key={p.name} title={p.name}
            onClick={() => onTheme(Object.assign({}, theme, { colors: Object.assign({}, theme.colors, p.colors) }))}>
            <span style={{ background: p.colors.primary }} />
            <span style={{ background: p.colors.secondary }} />
            <span style={{ background: p.colors.accent }} />
            <em>{p.name}</em>
          </button>
        ))}
      </div>

      <div className="sp-label"><span>Colores</span></div>
      {Object.entries(theme.colors || {}).map(([k, v]) => (
        <Field key={k} label={{
          primary: 'Principal', secondary: 'Secundario', accent: 'Acento',
          text: 'Texto', muted: 'Texto suave', bg: 'Fondo', surface: 'Superficie', border: 'Bordes'
        }[k] || k}>
          <ColorInput value={v} onChange={(nv) => setColor(k, nv)} theme={{ colors: {} }} />
        </Field>
      ))}

      <div className="sp-label"><span>Combinaciones de fuentes</span></div>
      <div className="sp-pairs">
        {FONT_PAIRS.map((p) => (
          <button type="button" key={p.name}
            className={theme.fonts?.heading?.family === p.heading && theme.fonts?.body?.family === p.body ? 'on' : ''}
            onClick={() => onTheme(Object.assign({}, theme, {
              fonts: {
                heading: Object.assign({}, theme.fonts.heading, { family: p.heading }),
                body: Object.assign({}, theme.fonts.body, { family: p.body })
              }
            }))}>
            <strong style={{ fontFamily: p.heading }}>{p.name}</strong>
            <small style={{ fontFamily: p.body }}>{p.heading} + {p.body}</small>
          </button>
        ))}
      </div>

      <Field label="Fuente de títulos">
        <select className="sp-input" value={theme.fonts?.heading?.family} onChange={(e) => setFont('heading', e.target.value)}>
          {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      </Field>
      <Field label="Fuente de texto">
        <select className="sp-input" value={theme.fonts?.body?.family} onChange={(e) => setFont('body', e.target.value)}>
          {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      </Field>
      <Field label="Redondeo general">
        <NumberInput value={theme.radius} onChange={(v) => onTheme(Object.assign({}, theme, { radius: v ?? 12 }))} suffix="px" min={0} max={48} />
      </Field>
      <Field label="Ancho máximo del contenido">
        <NumberInput value={theme.maxWidth} onChange={(v) => onTheme(Object.assign({}, theme, { maxWidth: v ?? 1140 }))} suffix="px" min={640} max={1920} />
      </Field>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Panel
 * ------------------------------------------------------------------ */

export default function StylePanel(props) {
  const { node, breakpoint, theme } = props;
  const [tab, setTab] = useState('content');

  if (!node) {
    return <ThemePanel theme={theme} onTheme={props.onTheme} />;
  }

  return (
    <div className="sp">
      <div className="sp-head">
        <strong>{TYPE_LABELS[node.type] || node.type}</strong>
        <div className="sp-tabs">
          {[['content', 'Contenido'], ['style', 'Estilo'], ['advanced', 'Avanzado']].map(([k, label]) => (
            <button type="button" key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{label}</button>
          ))}
        </div>
      </div>
      <div className="sp-body">
        {tab === 'content' && <ContentTab node={node} setProps={props.setProps} theme={theme} calendars={props.calendars} />}
        {tab === 'style' && <StyleTab node={node} breakpoint={breakpoint} setStyle={props.setStyle} theme={theme} />}
        {tab === 'advanced' && <AdvancedTab node={node} setAdvanced={props.setAdvanced} />}
      </div>
    </div>
  );
}
